import { useCallback, useRef, useState } from 'react';

const LONG_PRESS_MS = 450;
const MOVEMENT_THRESHOLD = 10;

export function useLongPressSelection(onLongPress: () => void) {
  const [pressing, setPressing] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef({ x: 0, y: 0 });
  const suppressClick = useRef(false);
  const handled = useRef(false);

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setPressing(false);
  }, []);

  const complete = useCallback(
    (suppressNextClick = true) => {
      if (handled.current) return;
      cancel();
      handled.current = true;
      suppressClick.current = suppressNextClick;
      onLongPress();
      if ('vibrate' in navigator) navigator.vibrate?.(20);
    },
    [cancel, onLongPress],
  );

  const begin = useCallback(
    (x: number, y: number) => {
      start.current = { x, y };
      setPressing(true);
      suppressClick.current = false;
      handled.current = false;
      timer.current = setTimeout(complete, LONG_PRESS_MS);
    },
    [complete],
  );

  const move = useCallback(
    (x: number, y: number) => {
      if (
        Math.hypot(x - start.current.x, y - start.current.y) >
        MOVEMENT_THRESHOLD
      ) {
        cancel();
      }
    },
    [cancel],
  );

  const activate = useCallback((action: () => void) => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    action();
  }, []);

  return {
    pressing,
    activate,
    handlers: {
      onContextMenu: (event: React.MouseEvent) => {
        event.preventDefault();
        complete(false);
      },
      onTouchStart: (event: React.TouchEvent) =>
        begin(event.touches[0].clientX, event.touches[0].clientY),
      onTouchMove: (event: React.TouchEvent) =>
        move(event.touches[0].clientX, event.touches[0].clientY),
      onTouchEnd: cancel,
      onTouchCancel: cancel,
      onMouseDown: (event: React.MouseEvent) =>
        begin(event.clientX, event.clientY),
      onMouseMove: (event: React.MouseEvent) =>
        move(event.clientX, event.clientY),
      onMouseUp: cancel,
      onMouseLeave: cancel,
    },
  };
}
